import { useState, useEffect, useMemo, useCallback } from 'react';

import {
  Connector,
  ConnectorListItem,
  ConnectorCategory,
  CONNECTOR_CATEGORY_ORDER,
} from '@optidev/shared-types';

import { connectorsApi, ConnectorFilters } from '@/lib/api/connectors';
import { useAuthToken } from './useAuthToken';

// In-memory cache for connectors (shared across all hook instances)
let connectorsCache: ConnectorListItem[] | null = null;
let connectorsFetchPromise: Promise<ConnectorListItem[]> | null = null;

// Cache for individual connector details
const connectorDetailsCache: Map<string, Connector> = new Map();

/**
 * Hook to fetch and manage connectors list with optional filtering
 */
export function useConnectors(filters?: ConnectorFilters) {
  const { token, loading: tokenLoading } = useAuthToken();
  const [connectors, setConnectors] = useState<ConnectorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchConnectors = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      // Use cache only if no filters are applied
      if (!filters && connectorsCache) {
        setConnectors(connectorsCache);
        setLoading(false);
        return;
      }

      // If fetch already in progress and no filters, wait for it
      if (!filters && connectorsFetchPromise) {
        const data = await connectorsFetchPromise;
        setConnectors(data);
        setLoading(false);
        return;
      }

      // Start new fetch
      const fetchPromise = connectorsApi.getAll(token, filters);

      // Only cache unfiltered results
      if (!filters) {
        connectorsFetchPromise = fetchPromise;
      }

      const data = await fetchPromise;

      // Cache unfiltered results
      if (!filters) {
        connectorsCache = data;
        connectorsFetchPromise = null;
      }

      setConnectors(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch connectors'));
      console.error('Failed to fetch connectors:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    if (!tokenLoading && token) {
      fetchConnectors();
    } else if (!tokenLoading && !token) {
      setLoading(false);
    }
  }, [tokenLoading, token, fetchConnectors]);

  const refetch = useCallback(() => {
    // Clear cache on manual refetch
    if (!filters) {
      connectorsCache = null;
    }
    fetchConnectors();
  }, [filters, fetchConnectors]);

  return { connectors, loading: loading || tokenLoading, error, refetch };
}

/**
 * Hook to fetch a single connector by ID
 */
export function useConnector(id: string | undefined) {
  const { token, loading: tokenLoading } = useAuthToken();
  const [connector, setConnector] = useState<Connector | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!token || !id) {
      setLoading(false);
      return;
    }

    // Check cache first
    const cached = connectorDetailsCache.get(id);
    if (cached) {
      setConnector(cached);
      setLoading(false);
      return;
    }

    const fetchConnector = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await connectorsApi.getOne(token, id);
        connectorDetailsCache.set(id, data);
        setConnector(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch connector'));
        console.error('Failed to fetch connector:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConnector();
  }, [token, id, tokenLoading]);

  const refetch = useCallback(async () => {
    if (!token || !id) return;

    connectorDetailsCache.delete(id);
    setLoading(true);
    setError(null);

    try {
      const data = await connectorsApi.getOne(token, id);
      connectorDetailsCache.set(id, data);
      setConnector(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch connector'));
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  return { connector, loading: loading || tokenLoading, error, refetch };
}

/**
 * Hook to fetch featured connectors
 */
export function useFeaturedConnectors() {
  const { token, loading: tokenLoading } = useAuthToken();
  const [connectors, setConnectors] = useState<ConnectorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const fetchFeatured = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await connectorsApi.getFeatured(token);
        setConnectors(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch featured connectors'));
        console.error('Failed to fetch featured connectors:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatured();
  }, [token, tokenLoading]);

  return { connectors, loading: loading || tokenLoading, error };
}

/**
 * Hook to fetch popular connectors
 */
export function usePopularConnectors() {
  const { token, loading: tokenLoading } = useAuthToken();
  const [connectors, setConnectors] = useState<ConnectorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const fetchPopular = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await connectorsApi.getPopular(token);
        setConnectors(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch popular connectors'));
        console.error('Failed to fetch popular connectors:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPopular();
  }, [token, tokenLoading]);

  return { connectors, loading: loading || tokenLoading, error };
}

/**
 * Hook to get connectors grouped by category
 */
export function useConnectorCategories() {
  const { connectors, loading, error, refetch } = useConnectors();

  const categorizedConnectors = useMemo(() => {
    // Built from CONNECTOR_CATEGORY_ORDER rather than a hand-written literal: a category added
    // to the union used to be silently absent here.
    const grouped = Object.fromEntries(
      CONNECTOR_CATEGORY_ORDER.map((category) => [category, [] as ConnectorListItem[]]),
    ) as Record<ConnectorCategory, ConnectorListItem[]>;

    connectors.forEach((connector) => {
      if (grouped[connector.category]) {
        grouped[connector.category].push(connector);
      }
    });

    return grouped;
  }, [connectors]);

  const categories = useMemo(() => {
    return Object.entries(categorizedConnectors)
      .filter(([, items]) => items.length > 0)
      .map(([category, items]) => ({
        category: category as ConnectorCategory,
        connectors: items,
        count: items.length,
      }));
  }, [categorizedConnectors]);

  return { categories, categorizedConnectors, loading, error, refetch };
}

/**
 * Hook for searching connectors with debounced query
 */
export function useConnectorSearch(query: string, debounceMs: number = 300) {
  const { token, loading: tokenLoading } = useAuthToken();
  const [results, setResults] = useState<ConnectorListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!token || !query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const data = await connectorsApi.search(token, query.trim());
        setResults(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Search failed'));
        console.error('Failed to search connectors:', err);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [token, query, debounceMs, tokenLoading]);

  return { results, loading: loading || tokenLoading, error };
}

/**
 * Clear all connector caches (useful after mutations)
 */
export function clearConnectorCache(): void {
  connectorsCache = null;
  connectorsFetchPromise = null;
  connectorDetailsCache.clear();
}
